import { useCallback, useContext, useEffect, useState } from "react";
import { api } from "../../../api";
import { OverflowFrame } from "../../../externalComponents";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { PageHeading } from "../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../externalComponents/SettingsSidebar";
import {
  Description,
  ErrorMessage,
  Field,
  Label
} from "../../../externalComponents/tailwind/fieldset";
import SelectInput from "../../../externalComponents/tailwind/select-input";
import {
  SwitchField,
  SwitchOnOff
} from "../../../externalComponents/tailwind/switch";
import { Text } from "../../../externalComponents/tailwind/text";
import { Textarea } from "../../../externalComponents/tailwind/textarea";
import { CustomerContext } from "../../../providers";

export default function WhatsappAwayMessagePage() {
  const { customer } = useContext(CustomerContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAwayMessageActivated, setIsAwayMessageActivated] = useState(false);
  const [awayMessage, setAwayMessage] = useState("");
  const [loadingCustomerData, setLoadingCustomerData] = useState(true);
  const [error, setError] = useState(null);
  const [messageError, setMessageError] = useState(false);
  const defaultOpeningHours = [
    { day: "Monday", dayOfWeek: 1, startHour: 9, endHour: 18, isOpen: true },
    { day: "Tuesday", dayOfWeek: 2, startHour: 9, endHour: 18, isOpen: true },
    { day: "Wednesday", dayOfWeek: 3, startHour: 9, endHour: 18, isOpen: true },
    { day: "Thursday", dayOfWeek: 4, startHour: 9, endHour: 18, isOpen: true },
    { day: "Friday", dayOfWeek: 5, startHour: 9, endHour: 18, isOpen: true },
    { day: "Saturday", dayOfWeek: 6, startHour: 0, endHour: 0, isOpen: false },
    { day: "Sunday", dayOfWeek: 0, startHour: 0, endHour: 0, isOpen: false },
  ];
  const [openingHours, setOpeningHours] = useState(defaultOpeningHours);

  useEffect(() => {
    if (customer) {
      const fetchCustomerData = async () => {
        try {
          const customerData = await api.customer.findById(customer.id, {
            select: {
              isAwayMessageActivated: true,
              awayMessage: true,
              openingHours: true,
            },
          });

          setIsAwayMessageActivated(
            customerData.isAwayMessageActivated ?? false,
          );
          setAwayMessage(customerData.awayMessage ?? "");

          // Si des horaires sont présents dans la base de données, utilisez-les
          if (
            customerData.openingHours &&
            customerData.openingHours.length > 0
          ) {
            // Utilisez les horaires de la DB et marquez les jours non renseignés comme fermés
            const dbOpeningHours = customerData.openingHours;

            // Créez un ensemble d'horaires par jour de la semaine
            const hoursMap = new Map(
              dbOpeningHours.map((item) => [item.dayOfWeek, item]),
            );

            // Mettez à jour avec les horaires de la DB
            const updatedOpeningHours = defaultOpeningHours.map((day) => {
              const dbHours = hoursMap.get(day.dayOfWeek);
              return dbHours
                ? { ...day, ...dbHours }
                : { ...day, isOpen: false };
            });

            setOpeningHours(updatedOpeningHours);
          } else {
            // Sinon, utilisez les horaires par défaut
            setOpeningHours(defaultOpeningHours);
          }
        } catch (err) {
          setError(err);
        } finally {
          setLoadingCustomerData(false);
        }
      };

      fetchCustomerData();
    }
  }, [customer]);

  const handleAwayMessageChange = useCallback((e) => {
    setAwayMessage(e.target.value);
    setIsEditing(true);
  }, []);

  const saveGeneralSettings = async () => {
    setIsLoading(true);
    setMessageError(false);
    if (isAwayMessageActivated && !awayMessage) {
      setMessageError(true);
      setIsLoading(false);
      return;
    }

    const openingHoursJson = openingHours.map(
      ({ day, dayOfWeek, startHour, endHour, isOpen }) => ({
        day,
        dayOfWeek,
        startHour,
        endHour,
        isOpen,
      }),
    );

    try {
      await api.customer.update(customer.id, {
        isAwayMessageActivated,
        awayMessage: awayMessage,
        openingHours: openingHoursJson,
      });
    } catch (err) {
      setError(err);
    } finally {
      setIsEditing(false);
      setIsLoading(false);
    }
  };

  const optionsHours = Array.from({ length: 25 }, (_, i) => ({
    value: i,
    label: `${i.toString().padStart(2, "0")}:00`,
  }));

  const handleChangeOpeningHours = (day, field, value) => {
    setOpeningHours((prev) =>
      prev.map((item) =>
        item.day === day ? { ...item, [field]: value } : item,
      ),
    );
    setIsEditing(true);
  };

  if (loadingCustomerData) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <>
      {console.log(openingHours)}
      <SettingsSidebar>
        <PageHeading
          title="Away Message"
          primaryAction="Save"
          handlePrimaryAction={saveGeneralSettings}
          isPrimaryLoading={isLoading}
          // isPrimaryDisabled={!isEditing}
          // secondaryAction="Change settings"
          // handleSecondaryAction={handleModify}
          // isSecondaryDisabled={isEditing}
        />
        <OverflowFrame>
          <div className="my-5">
            {customer?.isSubscribed &&
              (customer?.channelId360Dialog || customer?.waAccessToken) && (
                <>
                  <Field>
                    <Description>
                      Set up your automatic response for when you're not
                      available. <br />
                      This message will be sent to customers who contact you
                      outside of business hours.
                      <br />
                      If you also have a general chatbot, the away message will
                      be sent in proririty.
                    </Description>
                    <div className="flex content-center items-center mt-5">
                      <SwitchField className="w-64">
                        <Label>
                          {`${
                            isAwayMessageActivated ? "Deactivate" : "Activate"
                          }`}{" "}
                          away message
                        </Label>
                        <SwitchOnOff
                          name="activate_away_message"
                          checked={isAwayMessageActivated}
                          onChange={(checked) => {
                            setIsAwayMessageActivated(checked);
                            setIsEditing(true);
                          }}
                        ></SwitchOnOff>
                      </SwitchField>
                      {isEditing && (
                        <Text className="text-pink text-xs ml-3">
                          Unsaved settings changes
                        </Text>
                      )}
                    </div>
                    {isAwayMessageActivated && (
                      <>
                        <div className="border rounded-customBorderRadius my-2 p-3">
                          <Label>
                            Set your opening hours (Europe/Paris timezone)
                          </Label>
                          <div className="flex grow justify-between ">
                            {openingHours.map((dayHours) => (
                              <div
                                key={dayHours.day}
                                className="w-[14%] p-1 flex flex-col"
                              >
                                <Label className="mb-2">{dayHours.day}</Label>
                                <SwitchOnOff
                                  className="mb-2"
                                  name={`${dayHours.day.toLowerCase()}-open`}
                                  checked={dayHours.isOpen}
                                  onChange={(checked) =>
                                    handleChangeOpeningHours(
                                      dayHours.day,
                                      "isOpen",
                                      checked,
                                    )
                                  }
                                />
                                {dayHours.isOpen && (
                                  <>
                                    <div className="mb-2">
                                      <SelectInput
                                        options={optionsHours}
                                        selected={dayHours.startHour}
                                        onChange={(value) =>
                                          handleChangeOpeningHours(
                                            dayHours.day,
                                            "startHour",
                                            value,
                                          )
                                        }
                                        placeholder="Start Time"
                                      />
                                    </div>
                                    {console.log(dayHours.startHour)}
                                    <SelectInput
                                      options={optionsHours}
                                      selected={dayHours.endHour}
                                      onChange={(value) => {
                                        handleChangeOpeningHours(
                                          dayHours.day,
                                          "endHour",
                                          value,
                                        );
                                        setIsEditing(true);
                                      }}
                                      placeholder="End Time"
                                    />
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                          <div className="mt-5">
                            <Label>Set your away message</Label>
                            <Description>
                              Note: Please do not include variables in the away
                              message.
                            </Description>
                            <Textarea
                              name="away-message"
                              value={awayMessage}
                              onChange={handleAwayMessageChange}
                              invalid={messageError}
                            />
                            {messageError && (
                              <ErrorMessage>Message required</ErrorMessage>
                            )}
                          </div>
                        </div>
                        {console.log(awayMessage)}
                      </>
                    )}
                  </Field>
                </>
              )}
          </div>
        </OverflowFrame>
      </SettingsSidebar>
    </>
  );
}
